// vcard-parser.js — minimal vCard (.vcf) parser for DropPin contact import.
//
// v0.39.2 — initial. Handles vCard 2.1, 3.0 and 4.0 well enough for the
//           exports that iOS Contacts, Android Contacts and Google
//           Contacts produce. Not a full RFC 6350 implementation — we
//           skip the bits no construction operator will hit:
//             - vCard 2.1 QUOTED-PRINTABLE decoding (rare in modern exports)
//             - Grouping prefixes (e.g. "ITEM1.EMAIL:...")
//             - Binary PHOTO blobs
//             - Geo / TZ / categories
//
// What it DOES handle:
//   - Multi-card files (BEGIN:VCARD ... END:VCARD blocks)
//   - Line folding (continuation lines starting with space/tab)
//   - Parameterised properties (TEL;TYPE=CELL, EMAIL;TYPE=WORK,INTERNET)
//   - Phone-type preference (cell > work > home > anything)
//   - Email-type preference (work > home > anything)
//   - FN, N, EMAIL, TEL, ORG, TITLE, ROLE, NOTE
//
// Returns: an array of {name, email, phone, company, trade, notes} objects
// matching the shape POST /api/contacts expects. Empty strings are
// returned as null so the API doesn't store blanks.

const TRADE_HINTS = [
  // Map common job titles in a vCard ROLE/TITLE field to our trade keys.
  // Keys are lowercase substrings; values are TRADES keys in src/utils/trades.js.
  { needle: 'electric',  trade: 'electrical' },
  { needle: 'sparky',    trade: 'electrical' },
  { needle: 'plumb',     trade: 'plumbing'   },
  { needle: 'carpenter', trade: 'carpentry'  },
  { needle: 'chippy',    trade: 'carpentry'  },
  { needle: 'paint',     trade: 'painting'   },
  { needle: 'tile',      trade: 'tiling'     },
  { needle: 'tiler',     trade: 'tiling'     },
  { needle: 'hvac',      trade: 'hvac'       },
  { needle: 'air con',   trade: 'hvac'       },
  { needle: 'aircon',    trade: 'hvac'       },
  { needle: 'mechanic',  trade: 'hvac'       },
  { needle: 'roof',      trade: 'roofing'    },
  { needle: 'glaz',      trade: 'glazing'    },
  { needle: 'window',    trade: 'glazing'    },
  { needle: 'floor',     trade: 'flooring'   },
];

function guessTrade(text) {
  if (!text) return null;
  const t = String(text).toLowerCase();
  for (const h of TRADE_HINTS) {
    if (t.includes(h.needle)) return h.trade;
  }
  return null;
}

// Unfold continuation lines (RFC 6350 §3.2): a line starting with a single
// space or tab is a continuation of the previous line.
function unfold(text) {
  const lines = String(text || '').split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    if ((line.startsWith(' ') || line.startsWith('\t')) && out.length) {
      out[out.length - 1] += line.slice(1);
    } else {
      out.push(line);
    }
  }
  return out;
}

// Parse a single "PROP[;params]:value" line into { name, params, value }.
function parseLine(line) {
  const colonIdx = line.indexOf(':');
  if (colonIdx < 0) return null;
  const head = line.slice(0, colonIdx);
  const value = line.slice(colonIdx + 1);
  const parts = head.split(';');
  const name = parts[0].toUpperCase().replace(/^[A-Z0-9]+\./, ''); // strip group prefix like "ITEM1."
  const params = {};
  for (const p of parts.slice(1)) {
    const eq = p.indexOf('=');
    if (eq < 0) {
      // bare TYPE token (vCard 2.1 style, e.g. "TEL;CELL:...").
      params.TYPE = (params.TYPE ? params.TYPE + ',' : '') + p;
    } else {
      const k = p.slice(0, eq).toUpperCase();
      const v = p.slice(eq + 1);
      params[k] = (params[k] ? params[k] + ',' : '') + v;
    }
  }
  return { name, params, value };
}

function decodeValue(value) {
  // Basic unescape (RFC 6350 §3.4): \\, \;, \,, \n, \N
  return String(value || '')
    .replace(/\\\\/g, '\\')
    .replace(/\\;/g, ';')
    .replace(/\\,/g, ',')
    .replace(/\\n/gi, '\n');
}

// Type-preference scoring — lower number = better.
function scorePhone(typeParam) {
  const t = String(typeParam || '').toLowerCase();
  if (t.includes('cell') || t.includes('mobile')) return 0;
  if (t.includes('work'))                          return 1;
  if (t.includes('home'))                          return 2;
  return 3;
}
function scoreEmail(typeParam) {
  const t = String(typeParam || '').toLowerCase();
  if (t.includes('work')) return 0;
  if (t.includes('home')) return 1;
  return 2;
}

function parseCard(block) {
  const lines = unfold(block);
  let name = null, email = null, phone = null, company = null, role = null, notes = null;
  let bestPhoneScore = 99, bestEmailScore = 99;
  let nField = null; // fallback for FN
  for (const rawLine of lines) {
    if (!rawLine.trim()) continue;
    if (/^BEGIN:VCARD/i.test(rawLine) || /^END:VCARD/i.test(rawLine)) continue;
    if (/^VERSION:/i.test(rawLine)) continue;
    const parsed = parseLine(rawLine);
    if (!parsed) continue;
    const value = decodeValue(parsed.value).trim();
    if (!value) continue;

    switch (parsed.name) {
      case 'FN':
        name = value;
        break;
      case 'N':
        // N is Family;Given;Middle;Prefix;Suffix
        if (!nField) nField = value.split(';');
        break;
      case 'EMAIL': {
        const s = scoreEmail(parsed.params.TYPE);
        if (s < bestEmailScore) { email = value; bestEmailScore = s; }
        break;
      }
      case 'TEL': {
        const s = scorePhone(parsed.params.TYPE);
        if (s < bestPhoneScore) { phone = value; bestPhoneScore = s; }
        break;
      }
      case 'ORG':
        // ORG can be "Company;Department"
        company = value.split(';')[0].trim() || null;
        break;
      case 'TITLE':
      case 'ROLE':
        if (!role) role = value;
        break;
      case 'NOTE':
        notes = value;
        break;
      default:
        break;
    }
  }

  // FN fallback — assemble from N if FN is missing.
  if (!name && nField) {
    const [family, given, middle] = nField;
    name = [given, middle, family].filter(Boolean).join(' ').trim() || null;
  }

  if (!name) return null; // nothing useful

  return {
    name,
    email: email || null,
    phone: phone || null,
    company: company || null,
    trade: guessTrade(role) || guessTrade(company),
    notes: notes || null,
  };
}

/**
 * Parse a vCard file's contents into an array of contact records.
 * @param {string} text  raw contents of a .vcf file (UTF-8)
 * @returns {Array<{name, email, phone, company, trade, notes}>}
 */
export function parseVCard(text) {
  if (!text || typeof text !== 'string') return [];
  // Split into per-card blocks. Tolerate stray whitespace.
  const blocks = text.split(/BEGIN:VCARD/i).slice(1);
  const cards = [];
  for (const b of blocks) {
    const card = parseCard('BEGIN:VCARD' + b);
    if (card) cards.push(card);
  }
  return cards;
}
